import type { ExpenseSummary, ClientApprovalStatus } from '@peles/shared';
import { apiClient } from './client';

export interface ClientEvent {
  id: string;
  name: string;
  startAt: string;
  endAt: string | null;
  status: string;
  eventTypeName: string | null;
  venueName: string | null;
  guestCount: number | null;
  budgetVisibility: string;
  clientRole: string;
  permissions: ClientEventPermissions;
}

export interface ClientEventPermissions {
  canApprovePlan: boolean;
  canApproveInvoice: boolean;
  canMakePayments: boolean;
  canMakeChanges: boolean;
  /** Allows the client to print (floor plan, timeline). */
  canPrint: boolean;
}

export interface ClientExpenseItem {
  id: string;
  eventId: string;
  vendorCategoryName: string | null;
  itemName: string | null;
  vendorName: string | null;
  price: number | null;
  statusName: string | null;
  statusColor: string | null;
  clientApproval: ClientApprovalStatus;
}

export interface VendorEvent {
  eventId: string;
  eventName: string;
  eventDate: string;
  eventTypeName: string | null;
  vendorName: string;
  categories: string[];
  totalQuoted: number;
  totalPaid: number;
  status: string;
}

export interface VendorExpenseItem {
  id: string;
  eventId: string;
  vendorCategoryName: string | null;
  itemName: string | null;
  price: number;
  statusName: string | null;
  statusColor: string | null;
  dueDate: string | null;
  comments: string | null;
  totalPaid: number;
  balance: number;
  payments: Array<{
    id: string;
    amount: number;
    paymentMethodName: string | null;
    paidAt: string;
    notes: string | null;
  }>;
}

export interface VendorTimelineEntry {
  id: string;
  time: string | null;
  title: string;
  location: string | null;
  vendorNote: string | null;
  notes: string | null;
  sectionName: string | null;
}

export interface VendorProfile {
  id: string;
  businessName: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
}

// Client Portal
export async function getClientEvents(): Promise<ClientEvent[]> {
  const { data } = await apiClient.get<ClientEvent[]>('/portal/client/events');
  return data;
}

export async function getClientExpenseSummary(eventId: string): Promise<ExpenseSummary> {
  const { data } = await apiClient.get<ExpenseSummary>(
    `/portal/client/events/${eventId}/expense-summary`,
  );
  return data;
}

export async function getClientExpenseItems(eventId: string): Promise<ClientExpenseItem[]> {
  const { data } = await apiClient.get<ClientExpenseItem[]>(
    `/portal/client/events/${eventId}/expenses`,
  );
  return data;
}

export async function updateClientApproval(
  expenseItemId: string,
  clientApproval: ClientApprovalStatus,
): Promise<{ id: string; clientApproval: ClientApprovalStatus }> {
  const { data } = await apiClient.patch<{ id: string; clientApproval: ClientApprovalStatus }>(
    `/portal/client/expenses/${expenseItemId}/approval`,
    { clientApproval },
  );
  return data;
}

// Vendor Portal
export async function getVendorEvents(): Promise<VendorEvent[]> {
  const { data } = await apiClient.get<VendorEvent[]>('/portal/vendor/events');
  return data;
}

export async function getVendorEventExpenses(eventId: string): Promise<VendorExpenseItem[]> {
  const { data } = await apiClient.get<VendorExpenseItem[]>(
    `/portal/vendor/events/${eventId}/expenses`,
  );
  return data;
}

export async function getVendorEventTimeline(eventId: string): Promise<VendorTimelineEntry[]> {
  const { data } = await apiClient.get<VendorTimelineEntry[]>(
    `/portal/vendor/events/${eventId}/timeline`,
  );
  return data;
}

export async function getVendorProfile(): Promise<VendorProfile> {
  const { data } = await apiClient.get<VendorProfile>('/portal/vendor/profile');
  return data;
}

export async function updateVendorProfile(
  updates: Partial<Pick<VendorProfile, 'businessName' | 'phone' | 'email' | 'website'>>,
): Promise<VendorProfile> {
  const { data } = await apiClient.patch<VendorProfile>('/portal/vendor/profile', updates);
  return data;
}

export async function uploadVendorDocument(
  eventId: string,
  doc: { fileName: string; documentType: string; expenseItemId?: string },
): Promise<{ id: string; fileName: string; documentType: string }> {
  const { data } = await apiClient.post<{ id: string; fileName: string; documentType: string }>(
    `/portal/vendor/events/${eventId}/documents`,
    doc,
  );
  return data;
}

export interface ClientEventDetail {
  id: string;
  name: string;
  startAt: string;
  endAt: string | null;
  status: string;
  guestCount: number | null;
  notes: string | null;
  budgetVisibility: string;
  eventTypeName: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  /** The requesting client's own per-event permissions. */
  permissions: ClientEventPermissions;
}

export interface ClientGuest {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  rsvpStatus: string | null;
  tableNumber: number | null;
  mealPreference: string | null;
  notes: string | null;
  maleCount: number;
  femaleCount: number;
  kidCount: number;
  familyGroup: string | null;
}

export interface ClientFloorPlanObject {
  id: string;
  objectType: string;
  tableNumber: number | null;
  label: string | null;
  x: number;
  y: number;
  widthFt: number | null;
  heightFt: number | null;
  rotation: number;
  color: string;
  opacity: number;
  seats: number | null;
  sortOrder: number;
  config: Record<string, unknown> | null;
}

export interface ClientFloorPlan {
  id: string;
  name: string;
  roomName: string | null;
  objects: ClientFloorPlanObject[];
}

export interface ClientPhoto {
  id: string;
  fileName: string;
  s3Key: string;
  takenAt: string | null;
  caption: string | null;
}

export interface ClientTimelineEntry {
  id: string;
  time: string | null;
  title: string;
  location: string | null;
  notes: string | null;
}

export interface ClientTimelineSection {
  id: string;
  name: string;
  entries: ClientTimelineEntry[];
}

export interface ClientTimeline {
  id: string;
  name: string;
  description: string | null;
  sections: ClientTimelineSection[];
}

export interface ClientChecklistItem {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
  notes: string | null;
}

export interface ClientChecklistSection {
  id: string;
  name: string;
  items: ClientChecklistItem[];
}

export interface ClientChecklist {
  id: string;
  name: string;
  description: string | null;
  sections: ClientChecklistSection[];
}

export async function getClientEventDetail(eventId: string): Promise<ClientEventDetail> {
  const { data } = await apiClient.get<ClientEventDetail>(
    `/portal/client/events/${eventId}/detail`,
  );
  return data;
}

export async function getClientEventGuests(eventId: string): Promise<ClientGuest[]> {
  const { data } = await apiClient.get<ClientGuest[]>(`/portal/client/events/${eventId}/guests`);
  return data;
}

export async function getClientEventFloorPlans(eventId: string): Promise<ClientFloorPlan[]> {
  const { data } = await apiClient.get<ClientFloorPlan[]>(
    `/portal/client/events/${eventId}/floor-plans`,
  );
  return data;
}

export async function getClientEventPhotos(eventId: string): Promise<ClientPhoto[]> {
  const { data } = await apiClient.get<ClientPhoto[]>(`/portal/client/events/${eventId}/photos`);
  return data;
}

export async function getClientEventTimeline(eventId: string): Promise<ClientTimeline[]> {
  const { data } = await apiClient.get<ClientTimeline[]>(
    `/portal/client/events/${eventId}/timeline`,
  );
  return data;
}

export async function getClientEventChecklist(eventId: string): Promise<ClientChecklist[]> {
  const { data } = await apiClient.get<ClientChecklist[]>(
    `/portal/client/events/${eventId}/checklist`,
  );
  return data;
}
